import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { DI_TOKENS, resolve } from '@src/di/container';
import {
  ActionStatus,
  ActionType,
  EVENT_CATEGORY_MAP,
  EventCategory,
  MonitoringService,
} from '@src/monitoring/types';
import { analytics } from '@src/utility/analytics';
import { makeAutoObservable } from 'mobx';
import smartlook from 'smartlook-client';

import type { RootStore } from './RootStore';
import type {
  ActionContext,
  ActionTrackingConfig,
  EnhancedTrackEventParams,
  MonitoringContextData,
  PerformanceMetrics,
  WorkflowType,
} from '@src/monitoring/types';

export class AnalyticsStore {
  private monitoringService: MonitoringService;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, {
      // rootStore: false,
    });

    // Initialize monitoring service with context provider
    this.monitoringService = new MonitoringService(() =>
      this.createMonitoringContextData(),
    );
  }

  // ===== ENHANCED ACTION TRACKING METHODS =====

  /**
   * Start tracking a user action
   * @param actionName - The specific action being performed
   * @param actionType - The hierarchical type of action
   * @param workflowId - Optional workflow ID for correlation
   * @param context - Optional additional context
   * @param metadata - Optional action-specific metadata
   * @returns Transaction ID for tracking the action
   */
  startUserAction(
    actionName: string,
    actionType: ActionType = ActionType.ACTION,
    workflowId?: string,
    context?: Partial<ActionContext>,
    metadata?: Record<string, any>,
  ): string {
    return this.monitoringService.startUserAction(
      actionName,
      actionType,
      workflowId,
      context,
      metadata,
    );
  }

  /**
   * Complete a user action successfully
   * @param transactionId - The transaction ID from startUserAction
   * @param metadata - Optional additional metadata
   * @param performance - Optional performance metrics
   */
  completeUserAction(
    transactionId: string,
    metadata?: Record<string, any>,
    performance?: Partial<PerformanceMetrics>,
  ): void {
    this.monitoringService.completeUserAction(
      transactionId,
      metadata,
      performance,
    );
  }

  /**
   * Fail a user action with error context
   * @param transactionId - The transaction ID from startUserAction
   * @param error - Error object or message
   * @param metadata - Optional additional metadata
   */
  failUserAction(
    transactionId: string,
    error: Error | string,
    metadata?: Record<string, any>,
  ): void {
    this.monitoringService.failUserAction(transactionId, error, metadata);
  }

  /**
   * Cancel a user action
   * @param transactionId - The transaction ID from startUserAction
   * @param reason - Optional cancellation reason
   */
  cancelUserAction(transactionId: string, reason?: string): void {
    this.monitoringService.cancelUserAction(transactionId, reason);
  }

  // ===== WORKFLOW TRACKING METHODS =====

  /**
   * Start a new workflow
   * @param workflowType - The type of workflow being started
   * @param metadata - Optional workflow metadata
   * @param context - Optional additional context
   * @returns Workflow ID for tracking related actions
   */
  startWorkflow(
    workflowType: WorkflowType,
    metadata?: Record<string, any>,
    context?: Partial<ActionContext>,
  ): string {
    return this.monitoringService.startWorkflow(
      workflowType,
      metadata,
      context,
    );
  }

  /**
   * Complete a workflow
   * @param workflowId - The workflow ID from startWorkflow
   * @param success - Whether the workflow completed successfully
   * @param metadata - Optional completion metadata
   */
  completeWorkflow(
    workflowId: string,
    success: boolean,
    metadata?: Record<string, any>,
  ): void {
    this.monitoringService.completeWorkflow(workflowId, success, metadata);
  }

  /**
   * Cancel a workflow
   * @param workflowId - The workflow ID from startWorkflow
   * @param reason - Optional cancellation reason
   */
  cancelWorkflow(workflowId: string, reason?: string): void {
    this.monitoringService.cancelWorkflow(workflowId, reason);
  }

  identifyUser() {
    const { userIdentityStore } = this.rootStore;
    const identity = userIdentityStore.currentIdentity;

    // TODO: Better env checlking, should be out of store too.
    if (process.env.NODE_ENV === 'production') {
      if (!identity) {
        console.warn('Attempting to identify analytics without user identity');
        return;
      }

      const userData = {
        id: identity.id,
        email: identity.email,
        name: identity.name,
        type: identity.type,
        source: identity.source,
      };

      console.log('Identifying user:', userData);

      // Datadog
      datadogRum.setUser(userData);
      datadogLogs.setUser(userData);

      // Segment
      analytics.identify(identity.id, userData);

      // Smartlook
      if (process.env.NODE_ENV === 'production') {
        // @ts-ignore
        smartlook.identify(identity.id, userData);
      }
    }
  }

  setContext(storyId: string, orgSlug: string | undefined) {
    // Datadog context
    datadogRum.setGlobalContextProperty('organization', orgSlug);
    datadogLogs.setGlobalContextProperty('organization', orgSlug);
    datadogRum.setGlobalContextProperty('storyId', storyId);
    datadogLogs.setGlobalContextProperty('storyId', storyId);

    // Segment page view
    analytics.page();
  }

  initSmartlook() {
    if (process.env.NODE_ENV === 'production') {
      smartlook.init(
        process.env.REACT_APP_SMARTLOOK_CONTENT_STUDIO_PROJECT_KEY as string,
      );
    }
  }

  getTrackedUserData() {
    const userData = this.rootStore.userIdentityStore.currentIdentity;
    return {
      ...(userData && {
        user: {
          email: userData.email,
          name: userData.name,
        },
      }),
    };
  }

  getTrackedEnvData() {
    return {
      env: process.env.REACT_APP_DATO_ENV || 'dev',
    };
  }

  /**
   * Enhanced track event method with backward compatibility
   * Supports both legacy string event names and new EventName enum
   */
  trackEvent({
    eventName,
    label,
    extraProperties = {},
    logMethod = 'log',
    transactionId,
    actionType = ActionType.ACTION,
    eventCategory,
    workflowId,
    status = ActionStatus.SUCCESS,
    performance,
    errorContext,
  }: EnhancedTrackEventParams) {
    // Legacy tracking (maintain backward compatibility)
    const trackedProperties = {
      datetime: new Date().toISOString(),
      ...this.getTrackedUserData(),
      ...this.getTrackedEnvData(),
      ...extraProperties,
    };

    // Determine event category for routing
    const determinedEventCategory =
      eventCategory || this.getEventCategory(eventName);

    // Route to appropriate analytics systems based on category
    this.routeEventToAnalyticsSystems(
      eventName,
      trackedProperties,
      determinedEventCategory,
    );

    // Legacy console logging
    const eventLog = {
      level: 'info',
      eventName,
      label,
      transactionId,
      eventCategory: determinedEventCategory,
      analyticsContext: {
        ...trackedProperties,
      },
    };
    console[logMethod](eventLog);

    // Enhanced tracking (new monitoring infrastructure)
    if (this.getMonitoringConfig().enabled && eventName) {
      try {
        const actionTransactionId =
          transactionId ||
          this.startUserAction(
            eventName,
            actionType,
            workflowId,
            undefined,
            extraProperties,
          );

        if (actionTransactionId) {
          // Handle different action statuses
          switch (status) {
            case ActionStatus.SUCCESS:
              this.completeUserAction(
                actionTransactionId,
                extraProperties,
                performance,
              );
              break;
            case ActionStatus.FAILURE:
              this.failUserAction(
                actionTransactionId,
                errorContext?.errorMessage || label,
                extraProperties,
              );
              break;
            case ActionStatus.CANCELLED:
              this.cancelUserAction(actionTransactionId, label);
              break;
            case ActionStatus.STARTED:
              // Action already started, no additional action needed
              break;
          }
        }
      } catch (error) {
        console.warn(
          'Enhanced tracking failed, falling back to legacy tracking:',
          error,
        );
      }
    }
  }

  // ===== CONFIGURATION AND UTILITY METHODS =====

  /**
   * Create monitoring context data from RootStore and VideoCreatorStore
   * Acts as an adapter between stores and monitoring utilities
   * Uses dependency injection to resolve VideoCreatorStore when available
   */
  private createMonitoringContextData(): MonitoringContextData {
    try {
      // Resolve RootStore from DI container first, fallback to constructor parameter
      let rootStore;
      try {
        rootStore = resolve(DI_TOKENS.ROOT_STORE);
      } catch (error) {
        // DI container not initialized yet, use constructor parameter
        rootStore = this.rootStore;
      }
      const userIdentity = rootStore.userIdentityStore?.currentIdentity;

      // Resolve VideoCreatorStore from DI container
      let videoCreator;
      try {
        videoCreator = resolve(DI_TOKENS.VIDEO_CREATOR_STORE);
      } catch (error) {
        // DI container not initialized yet or VideoCreatorStore not available
        console.debug(
          'VideoCreatorStore not available for context enrichment:',
          error,
        );
        videoCreator = null;
      }

      return {
        user: userIdentity
          ? {
              id: userIdentity.id,
              email: userIdentity.email,
              name: userIdentity.name,
              role: (userIdentity as any).role,
              type: (userIdentity as any).type,
              source: userIdentity.source,
              environment: rootStore.userIdentityStore?.environment,
            }
          : undefined,
        video: videoCreator
          ? {
              story: videoCreator.story
                ? {
                    id: videoCreator.story.id,
                    title: videoCreator.story.title,
                  }
                : undefined,
              currentVideo: videoCreator.currentVideo?.id
                ? {
                    id: videoCreator.currentVideo.id,
                    title: videoCreator.currentVideo.title,
                  }
                : undefined,
            }
          : undefined,
        organization: videoCreator?.organization
          ? {
              id: videoCreator.organization.id,
              name: videoCreator.organization.title || '',
              title: videoCreator.organization.title,
              slug: videoCreator.organization.slug,
            }
          : undefined,
      };
    } catch (error) {
      console.warn('Failed to create monitoring context data:', error);
      return {};
    }
  }

  /**
   * Update monitoring configuration
   */
  updateMonitoringConfig(newConfig: Partial<ActionTrackingConfig>): void {
    this.monitoringService.updateMonitoringConfig(newConfig);
  }

  /**
   * Get current monitoring configuration
   */
  getMonitoringConfig(): ActionTrackingConfig {
    return this.monitoringService.getMonitoringConfig();
  }

  /**
   * Track performance metrics
   */
  trackPerformance(
    actionName: string,
    metrics: PerformanceMetrics,
    context?: Partial<ActionContext>,
  ): void {
    this.monitoringService.trackPerformance(actionName, metrics, context);
  }

  /**
   * Get event category for routing to appropriate monitoring system
   */
  private getEventCategory(eventName: string): EventCategory {
    return EVENT_CATEGORY_MAP[eventName] || EventCategory.BEHAVIORAL;
  }

  /**
   * Route events to appropriate analytics systems based on category
   */
  private routeEventToAnalyticsSystems(
    eventName: string,
    trackedProperties: Record<string, any>,
    eventCategory: EventCategory,
  ): void {
    if (!eventName) {
      return;
    }

    // Behavioral and critical events -> Segment (Product Analytics)
    if (
      eventCategory === EventCategory.BEHAVIORAL ||
      eventCategory === EventCategory.CRITICAL
    ) {
      analytics.track(eventName, trackedProperties);
    }

    // Operational and critical -> Datadog (System Monitoring)
    if (
      eventCategory === EventCategory.OPERATIONAL ||
      eventCategory === EventCategory.CRITICAL
    ) {
      datadogRum.addAction(eventName, {
        ...trackedProperties,
        eventCategory,
        operationalFocus: true,
      });
    }
  }

  log({
    level,
    label,
    transactionId,
    msg,
    extraProperties,
  }: {
    level: 'debug' | 'info' | 'warn' | 'error';
    label: string;
    transactionId: string | null;
    msg: string;
    extraProperties: Record<string, any>;
  }) {
    const trackedProperties = {
      datetime: new Date().toISOString(),
      ...this.getTrackedUserData(),
      ...this.getTrackedEnvData(),
      ...extraProperties,
    };
    const eventLog = {
      level,
      label,
      transactionId,
      msg,
      analyticsContext: {
        ...trackedProperties,
      },
    };
    console[level](eventLog);
  }
}
